import { useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCollection } from "@/hooks/useCollection";
import { DEPTS, TASK_STATUS, PRIORITY } from "@/constants/crm";
import { PageHeader, Panel, StatusBadge, NativeSelect, PillTabs } from "@/components/crm/common";
import { DataTable } from "@/components/crm/DataTable";
import { FilterBar, applyFilters } from "@/components/crm/FilterBar";
import { FormDialog } from "@/components/crm/FormDialog";
import { FileBox } from "@/components/crm/Files";
import { useAuth } from "@/context/AuthContext";
import { PersonChip } from "@/components/crm/UserImage";

export default function Assignments() {
  const { items: tasks, create, update } = useCollection("tasks");
  const { items: projects } = useCollection("projects");
  const { can } = useAuth();
  const canEdit = can("Assignments", "edit");
  const [dept, setDept] = useState(DEPTS[0]);
  const [search, setSearch] = useState("");
  const [proj, setProj] = useState("");
  const [status, setStatus] = useState("");
  const [form, setForm] = useState(null);
  const rows = applyFilters(tasks.filter((t) => t.department === dept), search, ["deliverable", "assignee"], [{ key: "project_id", value: proj }, { key: "status", value: status }]);
  const projOpts = projects.map((p) => ({ value: p.id, label: p.name }));

  const fields = [
    { name: "project_id", label: "Project", type: "select", options: projOpts, required: true },
    { name: "department", label: "Department", type: "select", options: DEPTS, required: true },
    { name: "assignee", label: "Assigned artist / employee", required: true },
    { name: "team_lead", label: "Team Lead" },
    { name: "deliverable", label: "Deliverable", required: true, full: true },
    { name: "deadline", label: "Deadline", type: "date", required: true },
    { name: "priority", label: "Priority", type: "select", options: PRIORITY },
    { name: "dependency", label: "Depends on" },
    { name: "next_action", label: "Next action" },
    { name: "reference", label: "Reference links / attachments", type: "textarea" },
  ];
  const save = async (f) => {
    const body = { ...f, project_name: projects.find((p) => p.id === f.project_id)?.name };
    return !!(f.id ? await update(f.id, body) : await create({ status: "Pending", ...body }, "Task assigned"));
  };

  return (
    <>
      <PageHeader num="04" title="Work Assignment" subtitle="One project, clearly assigned departmental deliverables. Every task has an owner and deadline.">
        {can("Assignments", "create") && <Button data-testid="assign-task-button" onClick={() => setForm({ department: dept, priority: "Normal" })} className=""><Plus className="h-4 w-4 mr-1" />Assign Task</Button>}
      </PageHeader>
      <PillTabs testid="dept-tab" value={dept} onChange={setDept} tabs={DEPTS.map((d) => [d, d, tasks.filter((t) => t.department === d).length])} />
      <Panel title={`${dept} — tasks`}>
        <FilterBar testid="tasks" search={search} setSearch={setSearch} filters={[
          { key: "project", label: "projects", value: proj, set: setProj, options: projOpts },
          { key: "status", label: "statuses", value: status, set: setStatus, options: TASK_STATUS },
        ]} />
        <DataTable testid="tasks-table" rows={rows} columns={[
          { key: "deliverable", label: "Deliverable", render: (r) => <div><div className="font-semibold text-slate-800">{r.deliverable}</div><div className="text-xs text-slate-500">{r.project_name}</div></div> },
          { key: "assignee", label: "Owner", render: (r) => <PersonChip name={r.assignee} /> },
          { key: "team_lead", label: "Team Lead", render: (r) => <PersonChip name={r.team_lead} /> },
          { key: "deadline", label: "Deadline" },
          { key: "priority", label: "Priority", render: (r) => <StatusBadge value={r.priority} /> },
          { key: "files", label: "Files / renders", render: (r) => <FileBox compact testid={`task-files-${r.id}`} entityType="task" entityId={r.id} canUpload={canEdit || can("Assignments", "view")} /> },
          { key: "status", label: "Status", render: (r) => canEdit ? <NativeSelect testid={`task-status-${r.id}`} value={r.status} onChange={(v) => update(r.id, { status: v })} options={TASK_STATUS} /> : <StatusBadge value={r.status} /> },
          { key: "act", label: "", render: (r) => canEdit && <Button data-testid={`task-reassign-${r.id}`} size="sm" variant="ghost" onClick={() => setForm(r)}>Reassign / Edit</Button> },
        ]} />
      </Panel>
      <p className="text-xs text-slate-500">Team Leads assign departmental tasks. The Project Lead coordinates dependencies across teams.</p>
      <FormDialog testid="task-form" open={!!form} onOpenChange={(o) => !o && setForm(null)} title={form?.id ? "Edit / reassign task" : "Assign task"} fields={fields} initial={form} onSubmit={save} />
    </>
  );
}
